import React from 'react';
import { Heart, Users, Leaf, Award } from 'lucide-react';
import { homestayInfo, facilities } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">About Us</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Our story of hospitality, warmth, and creating memorable experiences since {homestayInfo.established}
          </p>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-96 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1512818908771-583ff7531c14"
          alt="White Orchid Homestay"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1F4D3A] mb-4">Our Story</h2>
            </div>
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Established in <strong>{homestayInfo.established}</strong>, White Orchid Homestay was born from a passion to share the pristine beauty and tranquility of Kurseong with travelers seeking an escape from the hustle of city life.
              </p>
              <p>
                Located in the peaceful village of Chimney-Deorali, near the iconic Dowhill School, our homestay is surrounded by lush pine forests and offers spectacular views of Mt. Kanchenjunga. What started as a dream to create a home-away-from-home has blossomed into a beloved retreat for families, couples, and adventure enthusiasts.
              </p>
              <p>
                With a <strong>{homestayInfo.rating}-star rating</strong> on Google from over <strong>{homestayInfo.reviewCount} guests</strong>, we take pride in our personalized hospitality, clean and comfortable rooms, and most importantly, our fresh home-cooked food that guests rave about.
              </p>
              <p>
                At White Orchid, we believe in creating experiences, not just stays. From complimentary cycling tours through Dowhill forest to guided nature hikes, we ensure every guest leaves with cherished memories and a longing to return.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1F4D3A] mb-4">Our Values</h2>
            <p className="text-lg text-gray-600">What we stand for</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="border-[#A3B18A]/20 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1F4D3A] to-[#A3B18A] rounded-full flex items-center justify-center mx-auto">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Warm Hospitality</h3>
                <p className="text-gray-600">Treating every guest like family with personalized care and attention</p>
              </CardContent>
            </Card>
            <Card className="border-[#A3B18A]/20 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1F4D3A] to-[#A3B18A] rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Authenticity</h3>
                <p className="text-gray-600">Providing genuine local experiences and home-cooked traditional meals</p>
              </CardContent>
            </Card>
            <Card className="border-[#A3B18A]/20 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1F4D3A] to-[#A3B18A] rounded-full flex items-center justify-center mx-auto">
                  <Leaf className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Nature First</h3>
                <p className="text-gray-600">Preserving the natural beauty around us for future generations</p>
              </CardContent>
            </Card>
            <Card className="border-[#A3B18A]/20 hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1F4D3A] to-[#A3B18A] rounded-full flex items-center justify-center mx-auto">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#1F4D3A]">Excellence</h3>
                <p className="text-gray-600">Maintaining high standards of cleanliness, hygiene, and service quality</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1F4D3A] mb-4">Our Facilities</h2>
              <p className="text-lg text-gray-600">Everything you need for a comfortable stay</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {facilities.map((facility, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <div className="w-2 h-2 bg-[#1F4D3A] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700">{facility}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Guests Love Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#1F4D3A] mb-4">Why Guests Love Us</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-[#F7F5F0] border-none">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4A373] mb-2">{homestayInfo.rating}</div>
                  <p className="text-gray-700 font-medium">Average Rating</p>
                  <p className="text-sm text-gray-600 mt-1">On Google Reviews</p>
                </CardContent>
              </Card>
              <Card className="bg-[#F7F5F0] border-none">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4A373] mb-2">{homestayInfo.reviewCount}+</div>
                  <p className="text-gray-700 font-medium">Happy Guests</p>
                  <p className="text-sm text-gray-600 mt-1">Have Stayed With Us</p>
                </CardContent>
              </Card>
              <Card className="bg-[#F7F5F0] border-none">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-[#D4A373] mb-2">100%</div>
                  <p className="text-gray-700 font-medium">Satisfaction</p>
                  <p className="text-sm text-gray-600 mt-1">Clean & Hygienic</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Location Advantage */}
      <section className="py-16 bg-gradient-to-br from-[#1F4D3A] to-[#2d6b52] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-4xl font-bold mb-4">Our Location Advantage</h2>
            <p className="text-lg text-white/90 leading-relaxed">
              Situated directly on the main Dowhill-Baghgora road, White Orchid Homestay offers <strong>excellent road accessibility</strong> with <strong>no walking or climbing required</strong>. This makes us ideal for elderly guests and ensures easy luggage access. Despite being on the main road, we're nestled in a quiet village atmosphere surrounded by pristine pine forests.
            </p>
            <p className="text-lg text-white/90 leading-relaxed">
              Experience the best of both worlds - peaceful seclusion with convenient access.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F7F5F0]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1F4D3A] mb-4">
            Experience Our Hospitality
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join our family of happy guests and create your own unforgettable memories
          </p>
          <button
            onClick={() => {
              const message = encodeURIComponent("Hello! I'd like to book a stay at White Orchid Homestay.");
              window.open(`https://wa.me/${homestayInfo.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
            }}
            className="bg-[#25D366] hover:bg-[#20BA5A] text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            Book Your Stay on WhatsApp
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;
